import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { handleEnterPlatformDuel } from "../../api-clients/tournamentsClient";
import { StakeCurrency } from "@nestcoinco/hyper-api-gateway-api-client";
import { QUERY_KEYS } from "../constants";
import { useSetAtom } from "jotai";
import { duelEntryAtomWithStorage } from "../../jotai/atoms/tournament";
import { GameFlowState, GameType, ROUTES } from "../../utils/constants";
import {
  didUserClickToInitiateGameAtom,
  gameFlowStateAtomWithStorage,
  gameTypeAtom,
} from "../../jotai/atoms/games";
import useAnalytics from "../../hooks/useAnalytics";

function usePlatformDuelEntryMutation(redirectPath?: string) {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const setDuelEntry = useSetAtom(duelEntryAtomWithStorage);
  const setGameFlowState = useSetAtom(gameFlowStateAtomWithStorage);
  const setGameType = useSetAtom(gameTypeAtom);
  const setDidUserClickToInitiateGame = useSetAtom(
    didUserClickToInitiateGameAtom
  );

  const mutation = useMutation({
    mutationKey: [QUERY_KEYS.TOURNAMENT.DUEL_ENTRY],
    mutationFn: handleEnterPlatformDuel,
    onSuccess: (data) => {
      const { id, game, stakeAmount } = data.tournament;
      analytics.sendInitiateGameEvent({
        entry_fee: stakeAmount ?? 0,
        game_name: game.name,
        game_mode: GameType.BATTLE,
      });
      setDuelEntry(data);
      setGameType(GameType.BATTLE);
      const path = redirectPath || `${ROUTES.GAMES.ROOT}/${game.slug}/${id}`;
      setGameFlowState(GameFlowState.STARTING_GAME);
      navigate(path);
    },
  });

  const playBattleGame = useCallback(
    (
      gameId: string,
      currency = StakeCurrency.HPR,
      onSuccess?: () => void
    ) => {
      setDidUserClickToInitiateGame(true);
      mutation.mutate(
        { gameId, currency },
        { onSuccess: () => onSuccess?.() }
      );
    },
    [mutation, setDidUserClickToInitiateGame]
  );

  return { mutation, playBattleGame };
}

export default usePlatformDuelEntryMutation;
